import * as React from "react";
import { Eye, FileCheck2, Loader2, Pencil, Trash2 } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils.js";
import { Button, buttonVariants } from "./button.js";
import { Tooltip } from "./tooltip.js";

/**
 * What an upload field turns into once a file is on file: the file's name inside the
 * same field-shaped row, with an edit (replace) and a delete icon at its right end.
 * `onReplace` / `onDelete` each show their icon only when given, so a locked (verified)
 * file renders the same row with neither.
 */
const uploadedFileVariants = cva(
  "flex h-9 min-w-0 max-w-full items-center gap-2 rounded-md border pl-3 pr-1.5 text-[13px]",
  {
    variants: {
      tone: {
        success: "border-[var(--color-success)]/40 bg-[var(--color-success-bg)]",
        error: "border-[var(--color-error)]/40 bg-[var(--color-error-bg)]",
      },
    },
    defaultVariants: { tone: "success" },
  },
);

export interface UploadedFileProps extends VariantProps<typeof uploadedFileVariants> {
  name: string;
  /** Status pill after the name, e.g. "Rejected". */
  badge?: React.ReactNode;
  /** Something is happening to the file: both icons are off and the row shows `busyLabel`. */
  busy?: boolean | undefined;
  busyLabel?: string | undefined;
  accept?: string | undefined;
  /** The view icon opens the file; it is left out when this is not given. */
  onView?: (() => void) | undefined;
  /** Picking a file with the edit icon calls this with it. */
  onReplace?: ((file: File) => void) | undefined;
  onDelete?: (() => void) | undefined;
  className?: string | undefined;
}

export const uploadedFileDefaults = { viewLabel: "View file", replaceLabel: "Replace file", deleteLabel: "Delete file", busyLabel: "Uploading…" } as const;

export function UploadedFile({ name, tone, badge, busy, busyLabel = uploadedFileDefaults.busyLabel, accept, onView, onReplace, onDelete, className }: UploadedFileProps) {
  return (
    <div className={cn(uploadedFileVariants({ tone }), className)}>
      <span className={cn("shrink-0", tone === "error" ? "text-[var(--color-error-fg)]" : "text-[var(--color-success-fg)]")}>
        {busy ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <FileCheck2 size={15} aria-hidden="true" />}
      </span>
      <span className="min-w-0 flex-1 truncate font-medium text-surface-800" title={busy ? undefined : name}>
        {busy ? busyLabel : name}
      </span>
      {badge}
      {onView && (
        <Tooltip content={uploadedFileDefaults.viewLabel}>
          <Button size="icon" variant="ghost" aria-label={uploadedFileDefaults.viewLabel} disabled={busy} onClick={onView}>
            <Eye size={14} />
          </Button>
        </Tooltip>
      )}
      {onReplace && (
        <Tooltip content={uploadedFileDefaults.replaceLabel}>
          {/* The label is the icon button: the file input inside it opens the picker, and the focus ring follows the input. */}
          <label
            className={cn(
              buttonVariants({ variant: "ghost", size: "icon" }),
              "relative has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-500 has-[:disabled]:pointer-events-none has-[:disabled]:opacity-50",
            )}
          >
            <Pencil size={14} aria-hidden="true" />
            <input
              type="file"
              className="sr-only top-0 left-0"
              accept={accept ?? "image/*,.pdf"}
              disabled={busy}
              aria-label={uploadedFileDefaults.replaceLabel}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onReplace(file);
                e.target.value = ""; // so picking the same file again still fires
              }}
            />
          </label>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip content={uploadedFileDefaults.deleteLabel}>
          <Button
            size="icon"
            variant="ghost"
            aria-label={uploadedFileDefaults.deleteLabel}
            disabled={busy}
            onClick={onDelete}
            className="text-[var(--color-error-fg)] hover:bg-[var(--color-error-bg)] active:bg-[var(--color-error-bg)]"
          >
            <Trash2 size={14} />
          </Button>
        </Tooltip>
      )}
    </div>
  );
}
